import { useNavigate } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { Avatar, Button, Pill, TextLink } from '../../../ds/components';
import { useWorkspace } from '../../../lib/session';
import type { DealRecord, Requirement } from '../types';
import { NextStepCard, ParticipantsCard, PropertyCard, SideCard } from './bits';
import styles from './record.module.css';

/**
 * Deal-Akte 01 (gate card, KPI grid, open tasks, sidebar) and 02 (lost: banner, „Was danach passiert
 * ist", the dimmed KPI grid, „Verlustgrund").
 */

export interface OverviewTabProps {
  deal: DealRecord;
  onChangeStage: (targetStageId: string | null) => void;
}

/** Where a gate row can lead: the contact's editors, the units tab, the documents tab (financing signature is M6). */
function gateAction(requirement: Requirement, deal: DealRecord): { label: string; href: string } | null {
  if (requirement.state === 'met') return null;
  switch (requirement.key) {
    case 'self_disclosure_signed':
      return { label: 'Selbstauskunft senden', href: `/contacts/${deal.contact.id}?editor=selfDisclosure` };
    case 'unit_chosen':
      return { label: 'Einheit wählen', href: `/deals/${deal.id}?tab=units` };
    case 'reservation_active':
      return { label: 'Einheit reservieren', href: `/deals/${deal.id}?tab=units` };
    case 'documents_complete':
      return { label: 'Beim Kunden anfordern', href: `/contacts/${deal.contact.id}?editor=documents` };
    case 'notary_appointment':
      return { label: 'Termin buchen', href: `/contacts/${deal.contact.id}?editor=appointment` };
    default:
      return null;
  }
}

function GateRow({ requirement, hard, deal }: { requirement: Requirement; hard: boolean; deal: DealRecord }) {
  const navigate = useNavigate();
  const met = requirement.state === 'met';
  const action = gateAction(requirement, deal);
  return (
    <div className={styles.gateRow}>
      <span className={clsx(styles.gateMark, met && styles.gateMarkMet)} aria-hidden="true">
        {met ? '✓' : ''}
      </span>
      <span className={styles.gateText}>
        <span className={styles.gateLabel}>
          {requirement.label}
          <Pill variant={hard ? 'warning' : 'neutral'}>{hard ? 'harte Sperre' : 'Hinweis'}</Pill>
        </span>
        <span className={styles.gateDetail}>{requirement.detail}</span>
      </span>
      <span className={styles.gateOwner}>liefert {requirement.ownerLabel}</span>
      {action ? (
        <Button variant="secondary" size="small" onClick={() => void navigate(action.href)}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}

export function OverviewTab({ deal, onChangeStage }: OverviewTabProps) {
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const gate = deal.gate;

  if (deal.status === 'lost' && deal.lost) {
    const lost = deal.lost;
    return (
      <div className={styles.content}>
        <div className={styles.main}>
          <div className={styles.lostBanner} role="status">
            <div>
              <div className={styles.lostTitle}>Verloren · {lost.reasonLabel}</div>
              <div className={styles.lostLine}>{lost.line}</div>
            </div>
            <span className={styles.lostTag}>
              {deal.permissions.readOnlyText ?? 'Schreibgeschützt bis zum Wiederöffnen'}
            </span>
          </div>
          <section className={styles.card} aria-label="Was danach passiert ist">
            <div className={styles.cardTitle}>Was danach passiert ist</div>
            <p className={styles.cardIntro}>
              Der Deal lässt sich mit Grund wieder öffnen, dann startet er in der letzten Stage und die
              Einheit wird erneut reserviert, falls sie noch frei ist.
            </p>
            <div style={{ marginTop: 12 }}>
              {lost.followUps.map((item) => (
                <div key={item.title} className={styles.followRow}>
                  <span className={styles.followText}>
                    <div className={styles.followTitle}>{item.title}</div>
                    <div className={styles.followDetail}>{item.detail}</div>
                  </span>
                  {item.pill ? (
                    <Pill variant={item.pill === 'Frei' ? 'neutral' : 'info'}>{item.pill}</Pill>
                  ) : null}
                  {item.href ? (
                    <Button variant="secondary" size="small" onClick={() => void navigate(item.href ?? '')}>
                      Zur Kontaktakte
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
          <section className={clsx(styles.card, styles.cardDim)} aria-label="Kennzahlen">
            <div className={styles.kpis}>
              {deal.kpis.map((kpi) => (
                <div key={kpi.key} className={styles.kpi}>
                  <div className={styles.eyebrow}>{kpi.label}</div>
                  <div className={styles.kpiValue}>{kpi.value}</div>
                  {kpi.sub ? <div className={styles.kpiSub}>{kpi.sub}</div> : null}
                </div>
              ))}
            </div>
          </section>
        </div>
        <div className={styles.side}>
          <SideCard label="Verlustgrund">
            <div className={styles.nextStepTitle}>{lost.reasonLabel}</div>
            {lost.note ? (
              <div className={styles.quote}>
                „{lost.note}“<div className={styles.quoteBy}>Notiz von {lost.byShort ?? '—'}</div>
              </div>
            ) : null}
          </SideCard>
          <ParticipantsCard deal={deal} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <div className={styles.main}>
        {deal.cancelled ? (
          <div className={styles.lostBanner} role="status">
            <div>
              <div className={styles.lostTitle}>Storniert</div>
              <div className={styles.lostLine}>
                {deal.cancelled.line} · {deal.cancelled.reason}
              </div>
            </div>
          </div>
        ) : null}
        {gate ? (
          <section className={clsx(styles.gate, !gate.locked && styles.gateOpen)} aria-label={gate.title}>
            <div className={styles.gateHead}>
              <span className={styles.gateTitle}>{gate.title}</span>
              <span className={styles.gateSummary}>{gate.summary}</span>
              <span className={styles.gateLink}>
                {deal.permissions.canChangeStage ? (
                  <TextLink as="button" size="small" onClick={() => onChangeStage(gate.stageId)}>
                    Was prüft das Gate?
                  </TextLink>
                ) : null}
              </span>
            </div>
            {gate.hard.length + gate.hints.length > 0 ? (
              <div className={styles.gateBody}>
                {gate.hard.map((requirement) => (
                  <GateRow key={requirement.key} requirement={requirement} hard deal={deal} />
                ))}
                {gate.hints.map((requirement) => (
                  <GateRow key={requirement.key} requirement={requirement} hard={false} deal={deal} />
                ))}
              </div>
            ) : null}
          </section>
        ) : null}
        <section className={clsx(styles.card, styles.kpiCard)} aria-label="Kennzahlen">
          <div className={styles.kpis}>
            {deal.kpis.map((kpi) => (
              <div key={kpi.key} className={styles.kpi}>
                <div className={styles.eyebrow}>{kpi.label}</div>
                <div className={styles.kpiValue}>{kpi.value}</div>
                {kpi.sub ? <div className={styles.kpiSub}>{kpi.sub}</div> : null}
              </div>
            ))}
          </div>
        </section>
        <section className={styles.card} style={{ paddingBottom: 12 }} aria-label="Offene Tasks">
          <div className={styles.cardTitleRow}>
            <div className={styles.cardTitle}>Offene Tasks</div>
            <TextLink
              as="button"
              size="small"
              onClick={() => void navigate(`/contacts/${deal.contact.id}?editor=task`)}
            >
              Task anlegen
            </TextLink>
          </div>
          <div className={styles.tasks}>
            {deal.openTasks.length === 0 ? (
              <div className={styles.taskEmpty}>Keine offenen Tasks.</div>
            ) : null}
            {deal.openTasks.map((task) => {
              const mine = task.assignee.id === workspace.user.id;
              return (
                <button
                  key={task.id}
                  type="button"
                  className={styles.taskRow}
                  onClick={() => void navigate(task.href)}
                >
                  <span className={styles.taskBox} aria-hidden="true" />
                  <span className={styles.taskTitle}>{task.title}</span>
                  <span className={clsx(styles.taskDue, task.due.overdue && styles.taskDueOverdue)}>
                    {task.due.overdue
                      ? `überfällig seit ${task.due.on ? task.due.on.split('-').reverse().slice(0, 2).join('.') + '.' : ''}`
                      : task.due.label}
                  </span>
                  <Avatar
                    initials={task.assignee.initials}
                    name={task.assignee.name}
                    size={26}
                    tone={mine ? 'navy' : 'sand'}
                  />
                </button>
              );
            })}
          </div>
        </section>
      </div>
      <div className={styles.side}>
        <NextStepCard deal={deal} />
        <ParticipantsCard deal={deal} />
        <PropertyCard deal={deal} />
      </div>
    </div>
  );
}
